import { type InferResponseType, unwrap, withSignal } from "@nexxt/api-client";
import type { InvitationFunnelStage, LinkFilterStep } from "@nexxt/contracts/crm";
import { queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the campaigns of Guestlist (ADR-299): the list, a campaign, the rows of its matrix and its contacts,
// all read from the projection of crm. Everything of one campaign sits under `of(id)`, which its room reads again.

const campaigns = api.crm.campaigns;
const campaign = campaigns[":campaignId"];

export type CampaignSummary = InferResponseType<typeof campaigns.$get, 200>["items"][number];
export type Campaign = InferResponseType<typeof campaign.$get, 200>;
export type CampaignCustomer = InferResponseType<typeof campaign.invitations.$get, 200>["items"][number];
export type CampaignInvitation = CampaignCustomer["invitations"][number];
export type CampaignContact = InferResponseType<typeof campaign.contacts.$get, 200>["items"][number];

export interface CampaignListFilter {
  q?: string | undefined;
  archived?: true | undefined;
}

/**
 * The filters of the matrix and of the contacts: the customers with an invitation at a stage, or for a brand, or whose
 * booking link went at least as far as a step.
 */
export interface InvitationFilter {
  q?: string | undefined;
  stage?: InvitationFunnelStage | undefined;
  brand?: string | undefined;
  link?: LinkFilterStep | undefined;
}

/** A page of the matrix: enough customers for a screen, the next ones on request. */
const MATRIX_PAGE = 50;
/** The guests of another campaign to copy are read in the largest pages crm gives, up to a cap. */
const GUESTS_PAGE = 100;
const GUESTS_MAX_PAGES = 20;
/** The contacts a request of links can take at once, as booking does (1,000), in two pages of the dialog. */
const CONTACTS_PAGE = 500;

const keys = { all: ["campaigns"] as const };

const filterQuery = ({ q, stage, brand, link }: InvitationFilter) => ({
  ...(q && { q }),
  ...(stage && { stage }),
  ...(brand && { brandOrgId: brand }),
  ...(link && { linkStep: link }),
});

export const campaignQueries = {
  ...keys,
  lists: () => [...keys.all, "list"] as const,
  of: (campaignId: string) => [...keys.all, "of", campaignId] as const,

  list: ({ q, archived }: CampaignListFilter) =>
    queryOptions({
      queryKey: [...keys.all, "list", { q, archived }],
      queryFn: ({ signal }) =>
        unwrap(
          campaigns.$get(
            { query: { pageSize: "100", ...(q && { q }), ...(archived && { archived: "true" }) } },
            withSignal(signal),
          ),
        ),
    }),

  /** What ⌘K finds by name, archived left out: a few, the list has the rest (ADR-306). */
  find: (q: string) =>
    queryOptions({
      queryKey: [...keys.all, "find", q],
      queryFn: ({ signal }) => unwrap(campaigns.$get({ query: { q, pageSize: "5" } }, withSignal(signal))),
    }),

  /** The campaign with its funnel and its brands: the read that gives the grant of its room. */
  detail: (campaignId: string) =>
    queryOptions({
      queryKey: [...keys.all, "of", campaignId, "detail"],
      queryFn: ({ signal }) => unwrap(campaign.$get({ param: { campaignId } }, withSignal(signal))),
    }),

  invitations: (campaignId: string, filter: InvitationFilter, page: number) =>
    queryOptions({
      queryKey: [...keys.all, "of", campaignId, "invitations", filter, page],
      queryFn: ({ signal }) =>
        unwrap(
          campaign.invitations.$get(
            {
              param: { campaignId },
              query: { ...filterQuery(filter), page: String(page), pageSize: String(MATRIX_PAGE) },
            },
            withSignal(signal),
          ),
        ),
    }),

  /**
   * Who another campaign invited, to invite them here (ADR-306 §6): the customers of every page of its matrix, at a
   * stage or at any, up to `GUESTS_MAX_PAGES` pages; `capped` when there are more.
   */
  guests: (campaignId: string, stage: InvitationFunnelStage | undefined) =>
    queryOptions({
      queryKey: [...keys.all, "of", campaignId, "guests", stage],
      queryFn: async ({ signal }) => {
        const customers: { id: string; name: string }[] = [];
        for (let page = 1; page <= GUESTS_MAX_PAGES; page++) {
          const read = await unwrap(
            campaign.invitations.$get(
              {
                param: { campaignId },
                query: { ...(stage && { stage }), page: String(page), pageSize: String(GUESTS_PAGE) },
              },
              withSignal(signal),
            ),
          );
          customers.push(...read.items.map((row) => ({ id: row.customer.id, name: row.customer.name })));
          if (!read.hasMore) return { customers, capped: false };
        }
        return { customers, capped: true };
      },
    }),

  contacts: (campaignId: string, filter: InvitationFilter, page: number) =>
    queryOptions({
      queryKey: [...keys.all, "of", campaignId, "contacts", filter, page],
      queryFn: ({ signal }) =>
        unwrap(
          campaign.contacts.$get(
            {
              param: { campaignId },
              query: { ...filterQuery(filter), page: String(page), pageSize: String(CONTACTS_PAGE) },
            },
            withSignal(signal),
          ),
        ),
    }),
};
